import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || '';

/**
 * Parse standardized API error response and return user-facing message.
 * Expects JSON like: { status, error, message }.
 * @param {string} responseText - Raw response text from API
 * @param {string} fallback - Message when parsing fails or no message/error field
 * @returns {string}
 */
function getApiErrorMessage(responseText, fallback = 'Something went wrong') {
  if (!responseText || typeof responseText !== 'string') return fallback;
  const trimmed = responseText.trim();
  if (!trimmed) return fallback;
  try {
    const json = JSON.parse(trimmed);
    if (json && typeof json.message === 'string' && json.message) return json.message;
    if (json && typeof json.error === 'string' && json.error) return json.error;
  } catch (_) {
    // Not JSON – return raw text if it looks like a short message, else fallback
    return trimmed.length <= 200 ? trimmed : fallback;
  }
  return fallback;
}

/** Status values for worklist API */
export const WORKLIST_STATUS = {
  PENDING: 'PENDING',   // Active Worklists
  DRAFT: 'DRAFT',       // Approval Queue
  COMPLETED: 'COMPLETED', // Completed Worklists
};

/**
 * Fetch machine worklist by checklist review status.
 * @param {'PENDING'|'DRAFT'|'COMPLETED'} checklistReviewStatus
 * @returns {Promise<Array>} List of worklist items
 */
async function getMachineWorklistByStatus(checklistReviewStatus) {
  const url = `${API_BASE_URL}/api/v1/machine-workflow/get-machine-worklist-by-status?checklistReviewStatus=${encodeURIComponent(checklistReviewStatus)}`;
  const response = await fetchWithAuth(url, { method: 'GET' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, `Failed to fetch worklists (${response.status})`));
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch state list for state code lookup.
 * GET /api/v1/state-list
 * @returns {Promise<Array<{ stateId: number, stateCode: string, stateName: string }>>}
 */
async function getStateList() {
  const url = `${API_BASE_URL}/api/v1/state-list`;
  const response = await fetchWithAuth(url, { method: 'GET' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to fetch state list'));
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch all machine worklists for admin view.
 * GET /api/v1/machine-workflow/get-machine-worklists-admin
 */
async function getAdminMachineWorklists() {
  const url = `${API_BASE_URL}/api/v1/machine-workflow/get-machine-worklists-admin`;
  const response = await fetchWithAuth(url, { method: 'GET' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, `Failed to fetch admin machine worklists (${response.status})`));
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Approve machine workflow.
 * POST /api/v1/machine-workflow/approve-machine-workflow?machineWorkflowId={id}
 * Response: { status: 200, message: "Workflow approved successfully", machineWorkflowId }
 * @param {number|string} machineWorkflowId
 * @returns {Promise<{ status: number, message: string, machineWorkflowId: number }>}
 */
async function approveMachineWorkflow(machineWorkflowId) {
  const url = `${API_BASE_URL}/api/v1/machine-workflow/approve-machine-workflow?machineWorkflowId=${encodeURIComponent(machineWorkflowId)}`;
  const response = await fetchWithAuth(url, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to approve workflow'));
  }
  return response.json().catch(() => ({}));
}

/**
 * Reject machine workflow.
 * POST /api/v1/machine-workflow/reject-machine-workflow?machineWorkflowId={id}
 * @param {number|string} machineWorkflowId
 * @returns {Promise<Object>}
 */
async function rejectMachineWorkflow(machineWorkflowId) {
  const url = `${API_BASE_URL}/api/v1/machine-workflow/reject-machine-workflow?machineWorkflowId=${encodeURIComponent(machineWorkflowId)}`;
  const response = await fetchWithAuth(url, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to reject workflow'));
  }
  return response.json().catch(() => ({}));
}

/**
 * Approve tools & tackles workflow.
 * POST /api/v1/tools-tackles-workflow/approve-tools-tackles-workflow?toolsTacklesWorkflowId={id}
 * @param {number|string} toolsTacklesWorkflowId
 * @returns {Promise<{ status: number, message: string, toolsTacklesWorkflowId: number }>}
 */
async function approveToolsTacklesWorkflow(toolsTacklesWorkflowId) {
  const url = `${API_BASE_URL}/api/v1/tools-tackles-workflow/approve-tools-tackles-workflow?toolsTacklesWorkflowId=${encodeURIComponent(toolsTacklesWorkflowId)}`;
  const response = await fetchWithAuth(url, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to approve tools & tackles workflow'));
  }
  return response.json().catch(() => ({}));
}

/**
 * Reject tools & tackles workflow.
 * POST /api/v1/tools-tackles-workflow/reject-tools-tackles-workflow?toolsTacklesWorkflowId={id}
 * @param {number|string} toolsTacklesWorkflowId
 * @returns {Promise<Object>}
 */
async function rejectToolsTacklesWorkflow(toolsTacklesWorkflowId) {
  const url = `${API_BASE_URL}/api/v1/tools-tackles-workflow/reject-tools-tackles-workflow?toolsTacklesWorkflowId=${encodeURIComponent(toolsTacklesWorkflowId)}`;
  const response = await fetchWithAuth(url, { method: 'POST' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to reject tools & tackles workflow'));
  }
  return response.json().catch(() => ({}));
}

/**
 * Fetch all tools & tackles worklists for admin view.
 * GET /api/v1/tools-tackles-workflow/get-tools-tackles-worklists-admin
 */
async function getAdminToolsTacklesWorklists() {
  const url = `${API_BASE_URL}/api/v1/tools-tackles-workflow/get-tools-tackles-worklists-admin`;
  const response = await fetchWithAuth(url, { method: 'GET' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(getApiErrorMessage(text, 'Failed to fetch admin tools & tackles worklists'));
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

class WorklistsService {
  async getActiveWorklists() {
    return getMachineWorklistByStatus(WORKLIST_STATUS.PENDING);
  }

  async getApprovalQueue() {
    return getMachineWorklistByStatus(WORKLIST_STATUS.DRAFT);
  }

  async getCompletedWorklists() {
    return getMachineWorklistByStatus(WORKLIST_STATUS.COMPLETED);
  }

  /**
   * Generic get by status for list view.
   * @param {'PENDING'|'DRAFT'|'COMPLETED'} status
   */
  async getWorklistByStatus(status) {
    return getMachineWorklistByStatus(status);
  }

  /**
   * Admin view – all machine worklists in one list.
   */
  async getAdminMachineWorklists() {
    return getAdminMachineWorklists();
  }

  /**
   * Approve machine workflow.
   * POST /api/v1/machine-workflow/approve-machine-workflow?machineWorkflowId={id}
   */
  async approveMachineWorkflow(machineWorkflowId) {
    return approveMachineWorkflow(machineWorkflowId);
  }

  /**
   * Reject machine workflow.
   * POST /api/v1/machine-workflow/reject-machine-workflow?machineWorkflowId={id}
   */
  async rejectMachineWorkflow(machineWorkflowId) {
    return rejectMachineWorkflow(machineWorkflowId);
  }

  /**
   * State list for state code lookup (stateId -> stateCode).
   */
  async getStateList() {
    return getStateList();
  }

  /**
   * Admin view – all tools & tackles worklists in one list.
   */
  async getAdminToolsTacklesWorklists() {
    return getAdminToolsTacklesWorklists();
  }

  /**
   * Approve tools & tackles workflow.
   * POST /api/v1/tools-tackles-workflow/approve-tools-tackles-workflow?toolsTacklesWorkflowId={id}
   */
  async approveToolsTacklesWorkflow(toolsTacklesWorkflowId) {
    return approveToolsTacklesWorkflow(toolsTacklesWorkflowId);
  }

  /**
   * Reject tools & tackles workflow.
   * POST /api/v1/tools-tackles-workflow/reject-tools-tackles-workflow?toolsTacklesWorkflowId={id}
   */
  async rejectToolsTacklesWorkflow(toolsTacklesWorkflowId) {
    return rejectToolsTacklesWorkflow(toolsTacklesWorkflowId);
  }

  /**
   * Fetch inspection response for certificate display.
   * GET /api/v1/dummy/inspection-response
   * @returns {Promise<Object>} Certificate data (occupierName, factoryAddress, identificationDesc, inspectionPoints, etc.)
   */
  async getInspectionResponse() {
    const url = `${API_BASE_URL}/api/v1/dummy/inspection-response`;
    const response = await fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(getApiErrorMessage(text, `Failed to fetch inspection response (${response.status})`));
    }
    return response.json();
  }
}

export default new WorklistsService();
